import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { LAMPORTS_PER_SOL, PublicKey } from "@solana/web3.js";
import { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { connection } from "../../constants/constants";
import { getAssociatedTokenAddress, getAccount } from "@solana/spl-token";
import { useSelector } from "react-redux";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";

function useLocalWallet() {
  const [isOpen, setIsOpen] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [walletAddress, setWalletAddress] = useState("");
  const [balance, setBalance] = useState(0);
  const [balanceLAL, setBalanceLAL] = useState(0);
  const [isConnected, setIsConnected] = useState(null);
  const [tokenAddress, setTokenAddress] = useState(null);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState(-1);

  const { connected, publicKey, disconnect, connect } = useWallet();

  const wsRef = useRef(null);

  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;
  const role = user.role;

  const location = useLocation();

  useEffect(() => {
    if ("solana" in window && window.solana.isPhantom) {
      const provider = new PhantomWalletAdapter();
      setWallet(() => provider);
    } else {
      console.error("Wallet not detected");
    }
  }, []);

  const createWebSocket = () => {
    wsRef.current = new WebSocket(process.env.REACT_APP_API_ENDPOINT_URL);

    wsRef.current.onopen = () => {
      if (connected && publicKey) {
        const walletAddressMessage = {
          wallet: publicKey?.toBase58(),
          auth: token,
        };
        wsRef.current.send(
          JSON.stringify({
            type: "connect",
            walletAddress: walletAddressMessage,
          })
        );
      }
    };

    wsRef.current.onerror = (err) => {
      console.error("WebSocket connection error:", err);
    };

    wsRef.current.onclose = () => {
      // WebSocket connection closed
    };
  };

  useEffect(() => {
    const setDefaults = async () => {
      if (connected && publicKey) {
        localStorage.setItem("isWalletConnected", true);
        setWalletAddress(publicKey.toBase58());
        setIsConnected(publicKey);
        await updateBalance(publicKey);
        if (role === "user" && location.pathname === "/user-dashboard") {
          await updateBalanceLAL(balanceLAL);
        }
        if (
          role === "user" &&
          (location.pathname === "/user-dashboard" ||
            location.pathname === "/purchase")
        ) {
          const storedWallet = await fetchUserWalletAddress();
          if (!storedWallet) {
            const formData = new FormData();
            formData.append("walletAddress", publicKey?.toBase58());
            const response = await axios.post(
              `${process.env.REACT_APP_API_ENDPOINT_URL}/users/updateUserProfile`,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            if (response.data?.status === 401) {
              Swal.fire({
                title: "Error!",
                text: "Connected wallet address already used in other user profile. Please connect other wallet.",
                icon: "error",
                confirmButtonText: "OK",
              });
              return;
            }
          }
        }

        if (
          !wsRef.current ||
          wsRef.current.readyState === WebSocket.CLOSED ||
          wsRef.current.readyState === WebSocket.CLOSING
        ) {
          createWebSocket();
        } else if (wsRef.current.readyState === WebSocket.OPEN) {
          const walletAddressMessage = {
            wallet: publicKey?.toBase58(),
            auth: token,
          };
          wsRef.current.send(
            JSON.stringify({
              type: "connect",
              walletAddress: walletAddressMessage,
            })
          );
        } else if (wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.onopen = async () => {
            const walletAddressMessage = {
              wallet: publicKey?.toBase58(),
              auth: token,
            };
            wsRef.current.send(
              JSON.stringify({
                type: "connect",
                walletAddress: walletAddressMessage,
              })
            );
          };
        }
      } else {
        const walletAddressMessage = {
          wallet: publicKey?.toBase58(),
          auth: token,
        };
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "disconnect",
              walletAddress: walletAddressMessage,
            })
          );
          wsRef.current.close();
        } else {
          //WebSocket is not open, no need to send disconnect message
        }
        setWalletAddress("");
      }
    };
    if (connected && publicKey) {
      setDefaults();
    }

    // Cleanup function to handle disconnection
    return () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const walletAddressMessage = {
          wallet: publicKey?.toBase58(),
          auth: token,
        };
        wsRef.current.send(
          JSON.stringify({
            type: "disconnect",
            walletAddress: walletAddressMessage,
          })
        );
        wsRef.current.close();
      }
    };
  }, [connected, publicKey, role, location.pathname]);

  const updateBalance = async (publicKey) => {
    const balance = await connection.getBalance(publicKey);
    const updatedBalance = (balance / LAMPORTS_PER_SOL).toFixed(5);
    setBalance(updatedBalance);
  };

  const copyAccountToClipboard = () => {
    navigator.clipboard.writeText(walletAddress || "").then(
      function () {
        // Success function is intentionally left empty
      },
      function (err) {
        Swal.fire({
          title: "Error!",
          text: "Could not copy text: ",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    );
  };

  const handleRightToggle = () => setIsOpen(!isOpen);

  const fetchLFCPair = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/deployment/getLatestDeployedToken`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return new PublicKey(response.data.data?.tokenContractAddress || "");
    } catch (error) {
      console.error("Failed to fetch LFC pair:", error);
      throw error;
    }
  };

  const getTokenAddressAndInstruction = async (
    mint,
    owner,
    payer,
    connection
  ) => {
    let tokenAccountAddress = await getAssociatedTokenAddress(
      mint,
      owner,
      true
    );
    const accountInfo = await connection.getAccountInfo(tokenAccountAddress);
    if (accountInfo !== null) {
      return [tokenAccountAddress, null];
    }
    return [tokenAccountAddress];
  };

  const getUserLFCBalance = async (connection, userLfcTokenAccount) => {
    try {
      const userAccountInfo = await getAccount(
        connection,
        userLfcTokenAccount,
        "confirmed"
      );
      return userAccountInfo.amount;
    } catch (error) {
      console.error("Error getting LFC balance:", error);
      return 0;
    }
  };

  const updateBalanceLAL = async () => {
    if (!publicKey) return;

    const LFC_PAIR = await fetchLFCPair();
    const userLfcTokenAccount = await getTokenAddressAndInstruction(
      LFC_PAIR,
      publicKey,
      publicKey,
      connection
    );

    if (userLfcTokenAccount[0]) {
      const userLfcBalance = await getUserLFCBalance(
        connection,
        userLfcTokenAccount[0]
      );
      const updatedLALBalance = (Number(userLfcBalance) / 1000000).toFixed(5);
      setBalanceLAL(updatedLALBalance);
    } else {
      setBalanceLAL(0);
    }
  };

  const copyToClipboard = (wallet) => {
    navigator.clipboard.writeText(wallet || "").then(
      function () {
        // Success function is intentionally left empty
      },
      function (err) {
        Swal.fire({
          title: "Error!",
          text: "Could not copy text: ",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    );
  };

  const sortHistory = (filtered) => {
    if (sortField === "serialNumber") {
      const sorted = [...filtered];
      sorted.sort((a, b) => {
        const indexA = filtered.indexOf(a);
        const indexB = filtered.indexOf(b);
        return (indexA - indexB) * sortOrder;
      });
      return sorted;
    } else if (sortField === "createdAt") {
      const sorted = [...filtered];
      sorted.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return (dateB - dateA) * sortOrder; // descending order
      });
      return sorted;
    } else if (sortField) {
      const sorted = [...filtered];
      sorted.sort((a, b) => {
        const valueA = a[sortField];
        const valueB = b[sortField];
        if (valueA < valueB) return -1 * sortOrder;
        if (valueA > valueB) return 1 * sortOrder;
        return 0;
      });
      return sorted;
    }
    return filtered;
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const fetchUserWalletAddress = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/users/userWalletAddress`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const storedWalletAddress = response.data?.walletAddress?.walletAddress;
      return storedWalletAddress;
    } catch (error) {
      console.error("Failed to fetch user wallet address", error);
      throw error;
    }
  };

  return {
    isOpen,
    wallet,
    connected,
    walletAddress,
    balance,
    isConnected,
    tokenAddress,
    setTokenAddress,
    updateBalance,
    handleRightToggle,
    copyAccountToClipboard,
    setIsOpen,
    updateBalanceLAL,
    balanceLAL,
    copyToClipboard,
    sortField,
    sortOrder,
    sortHistory,
    onSort,
  };
}

export default useLocalWallet;
